import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  LLM_TASK_DEFINITIONS,
  isPersistableModelProvider,
  type ChatModel,
  type LlmTaskAssignment,
  type LlmTaskDefinition,
  type LlmTaskId,
} from '@reflex/shared';
import { getProvider } from '@reflex/plugin-api/models';
import { SELECTABLE_MODELS, MODEL_MAP, PROVIDER_LABELS } from '@reflex/ui/lib/model-icons';
import { SectionLabel, PageDescription } from '@reflex/ui/components/layout/Typography';
import { Card } from '@reflex/ui/components/ui/card';
import { Label } from '@reflex/ui/components/ui/label';
import { Switch } from '@reflex/ui/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@reflex/ui/components/ui/select';
import { ModelProviderSecretPicker } from '@reflex/ui/components/model-providers/ModelProviderSecretPicker';
import { findServiceManagedTaskModel } from '@/components/llm-tasks/service-managed-task-model';
import { useEnabledModels } from '@/hooks/config/useEnabledModels';
import { api } from '@/lib/api';
import { queryKeys, orgScope } from '@/lib/query-keys';

const DEFAULT_MODEL: ChatModel = 'haiku';

/**
 * `SELECTABLE_MODELS` grouped by provider, preserving the registry order
 * (anthropic, openai, google, xai). Mirrors the chat model selector so the
 * platform-task picker offers the same catalog.
 */
const MODELS_BY_PROVIDER: readonly { provider: string; models: typeof SELECTABLE_MODELS }[] =
  (() => {
    const order: string[] = [];
    const byProvider = new Map<string, typeof SELECTABLE_MODELS>();
    for (const model of SELECTABLE_MODELS) {
      if (!byProvider.has(model.provider)) {
        byProvider.set(model.provider, []);
        order.push(model.provider);
      }
      byProvider.get(model.provider)!.push(model);
    }
    return order.map((provider) => ({ provider, models: byProvider.get(provider)! }));
  })();

export interface LlmTaskAssignmentsManagerProps {
  orgId: string;
  /** Disables every input (used during stage transitions / loading windows). */
  readOnly?: boolean;
  /**
   * State shown for tasks that do not have a persisted assignment yet.
   * The onboarding wizard sets this to false while the org has no way to run
   * a task: no model-provider key and no service-managed default provider.
   */
  defaultEnabled?: boolean;
}

/**
 * Org-tier admin surface for platform LLM task assignments. Renders one
 * row per entry in `LLM_TASK_DEFINITIONS`: a label, a description, an
 * enable toggle, a model select, and a model-provider key picker.
 *
 * The list of tasks is static; rows are merged with any assignments
 * fetched from the server so a never-configured task still renders with
 * the platform-default selection.
 */
export function LlmTaskAssignmentsManager({
  orgId,
  readOnly = false,
  defaultEnabled = true,
}: LlmTaskAssignmentsManagerProps) {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.orgLlmTaskAssignments(orgScope(orgId));
  const assignmentsQuery = useQuery({
    queryKey,
    queryFn: () => api.listLlmTaskAssignments(orgId),
    enabled: !!orgId,
  });
  const byTaskId = useMemo(() => {
    const map = new Map<LlmTaskId, LlmTaskAssignment>();
    for (const row of assignmentsQuery.data ?? []) map.set(row.taskId, row);
    return map;
  }, [assignmentsQuery.data]);

  // Per-model key availability, mirroring the chat model selector. A model
  // is offered as enabled only when its provider has a usable key. While the
  // statuses load we treat every model as available so the menu never
  // flashes fully disabled.
  const modelStatuses = useEnabledModels();
  const modelHasKey = useMemo(() => {
    const statuses = modelStatuses.data;
    const byModel = new Map<ChatModel, boolean>(statuses?.map((s) => [s.id, s.hasKey]) ?? []);
    return (model: ChatModel) => (statuses ? (byModel.get(model) ?? false) : true);
  }, [modelStatuses.data]);
  // What an unconfigured task row displays as its model. The server resolves
  // such tasks against the deployment's service-managed provider first, so
  // showing its model here keeps the row honest about what actually runs.
  const defaultModel = findServiceManagedTaskModel(modelStatuses.data) ?? DEFAULT_MODEL;

  const upsertMutation = useMutation({
    mutationFn: ({
      taskId,
      enabled,
      model,
      providerSecretId,
    }: {
      taskId: LlmTaskId;
      enabled: boolean;
      model: ChatModel | null;
      providerSecretId: string | null;
    }) => api.upsertLlmTaskAssignment(orgId, taskId, { enabled, model, providerSecretId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      toast.success('LLM task updated');
    },
    onError: (err: Error) => toast.error('Failed to update LLM task', { description: err.message }),
  });

  return (
    <section className="space-y-3" data-testid="llm-task-assignments-manager">
      <SectionLabel>Platform LLM tasks</SectionLabel>
      <PageDescription className="mt-0">
        Pick the model and key Reflex uses for built-in LLM tasks like auto-titling new agent runs.
        Disable a task to fall back to a no-LLM heuristic.
      </PageDescription>
      <Card className="divide-y p-4">
        {LLM_TASK_DEFINITIONS.map((task) => (
          <LlmTaskRow
            key={task.id}
            task={task}
            assignment={byTaskId.get(task.id) ?? null}
            defaultEnabled={defaultEnabled}
            defaultModel={defaultModel}
            disabled={readOnly || upsertMutation.isPending}
            modelHasKey={modelHasKey}
            orgId={orgId}
            onChange={(next) =>
              upsertMutation.mutate({
                taskId: task.id,
                enabled: next.enabled,
                model: next.model,
                providerSecretId: next.providerSecretId,
              })
            }
          />
        ))}
      </Card>
    </section>
  );
}

function LlmTaskRow({
  task,
  assignment,
  defaultEnabled,
  defaultModel,
  disabled,
  modelHasKey,
  orgId,
  onChange,
}: {
  task: LlmTaskDefinition;
  assignment: LlmTaskAssignment | null;
  defaultEnabled: boolean;
  defaultModel: ChatModel;
  disabled: boolean;
  modelHasKey: (model: ChatModel) => boolean;
  orgId: string;
  onChange: (next: {
    enabled: boolean;
    model: ChatModel | null;
    providerSecretId: string | null;
  }) => void;
}) {
  const enabled = assignment ? assignment.enabled : defaultEnabled;
  const model: ChatModel = (assignment?.model as ChatModel | null) ?? defaultModel;
  const providerSecretId = assignment?.providerSecretId ?? null;
  // The key picker follows the selected model's provider. Its internal
  // reconcile effect clears a stale `providerSecretId` when the provider
  // changes, so a cross-provider model switch resets the key cleanly.
  const provider = getProvider(model);
  // Service-managed providers (e.g. Runloop) have no storable key rows, so
  // the picker would render permanently empty. The credential resolves
  // server-side from the deployment's service secret instead.
  const providerHasStorableKeys = isPersistableModelProvider(provider);

  return (
    <div className="space-y-3 py-3 first:pt-0 last:pb-0" data-testid={`llm-task-row-${task.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">{task.label}</p>
          <p className="text-xs text-muted-foreground">{task.description}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Label htmlFor={`llm-task-${task.id}-enabled`} className="text-xs text-muted-foreground">
            {enabled ? 'Enabled' : 'Disabled'}
          </Label>
          <Switch
            id={`llm-task-${task.id}-enabled`}
            checked={enabled}
            disabled={disabled}
            onCheckedChange={(next) =>
              onChange({
                enabled: next,
                model: next ? model : null,
                providerSecretId: next ? providerSecretId : null,
              })
            }
            aria-label={`Toggle ${task.label}`}
          />
        </div>
      </div>
      {enabled && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor={`llm-task-${task.id}-model`} className="text-xs">
              Model
            </Label>
            <Select
              value={model}
              disabled={disabled}
              onValueChange={(next) => {
                // The hidden key picker can't reconcile a stale id when the
                // target provider is service-managed — drop it here instead.
                const nextPersistable = isPersistableModelProvider(getProvider(next as ChatModel));
                onChange({
                  enabled,
                  model: next as ChatModel,
                  providerSecretId: nextPersistable ? providerSecretId : null,
                });
              }}
            >
              <SelectTrigger
                id={`llm-task-${task.id}-model`}
                className="h-9"
                data-testid={`llm-task-row-${task.id}-model`}
              >
                <SelectValue placeholder="Select a model">
                  {MODEL_MAP.get(model)?.name ?? model}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {MODELS_BY_PROVIDER.map((group) => (
                  <SelectGroup key={group.provider}>
                    <SelectLabel>{PROVIDER_LABELS[group.provider] ?? group.provider}</SelectLabel>
                    {group.models.map((m) => {
                      const hasKey = modelHasKey(m.id);
                      return (
                        <SelectItem
                          key={m.id}
                          value={m.id}
                          disabled={!hasKey}
                          data-testid={`llm-task-row-${task.id}-model-option-${m.id}`}
                        >
                          <span className="flex items-center gap-2">
                            <m.icon className="icon-sm" />
                            <span>{m.name}</span>
                            {!hasKey && (
                              <span className="text-2xs text-muted-foreground">· no key</span>
                            )}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Key</Label>
            {providerHasStorableKeys ? (
              <ModelProviderSecretPicker
                value={providerSecretId}
                onChange={(id) => onChange({ enabled, model, providerSecretId: id })}
                provider={provider}
                allowedTypes={['apiKey']}
                owner={{ ownerId: orgId }}
                disabled={disabled}
                className="w-full max-w-none"
                // `onChange` upserts immediately — disable sole-key / remembered
                // auto-select so navigating to Configure never writes (and toasts).
                autoSelectDefault={false}
              />
            ) : (
              <p
                className="flex h-9 items-center text-xs text-muted-foreground"
                data-testid={`llm-task-row-${task.id}-service-key`}
              >
                Managed by the service. No key needed.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
